// Kratos Venture Group — content data model (CMS-ready collections).
// Editable placeholders only. No invented stats, clients, testimonials, or claims.

export const company = {
  name: "Kratos Venture Group",
  brand: "KRATOS",
  descriptor: "VENTURE GROUP",
  tagline: "Business, reengineered.",
  // Editable placeholders — replace with confirmed details before launch.
  email: "hello@kratosventuregroup.com",
  phone: "",
  location: "",
  social: [
    { label: "LinkedIn", href: "#" },
    { label: "X", href: "#" },
    { label: "YouTube", href: "#" },
  ],
};

export const primaryNav = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Mythoros©", to: "/mythoros" },
  { label: "Industries", to: "/industries" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export const standardAnswers = {
  security:
    "Security depends on the final configuration, integrations, permission structure, hosting choices, and third-party providers. Kratos designs the deployment architecture around the client's operational and security requirements.",
  local:
    "Some functions may operate locally while others may use private cloud infrastructure or approved third-party AI services, depending on the selected architecture and business requirements.",
};

// Six homepage service modules (with geometric diagram keys).
export const serviceModules = [
  {
    id: "growth",
    diagram: "grid",
    title: "Business Growth Strategy",
    summary:
      "Positioning, offer design, and go-to-market planning grounded in how your business actually operates.",
  },
  {
    id: "ai",
    diagram: "orbit",
    title: "AI & Automation",
    summary:
      "Intelligent systems and automations that remove repetitive work and give your team leverage.",
  },
  {
    id: "sales",
    diagram: "funnel",
    title: "Sales & Lead Infrastructure",
    summary:
      "Structured pipelines, response systems, and follow-up that convert interest into revenue.",
  },
  {
    id: "crm",
    diagram: "hub",
    title: "CRM & Operations",
    summary:
      "Connected CRM and operational workflows that create visibility across teams and functions.",
  },
  {
    id: "voice",
    diagram: "wave",
    title: "Voice & Customer Engagement",
    summary:
      "Approved-scope voice and communication systems for consistent, scalable customer contact.",
  },
  {
    id: "tech",
    diagram: "stack",
    title: "Technology Implementation",
    summary:
      "Integrations and deployment that connect your tools into one coherent operating system.",
  },
];

// Full Services page (10 detailed services).
export const services = [
  {
    slug: "business-development",
    title: "Business Development",
    problem:
      "Growth stalls when opportunity, positioning, and execution are not aligned to a single operating model.",
    approach:
      "We assess your commercial engine end to end, then design a practical growth model your team can actually run.",
    deliverables: ["Opportunity map", "Positioning & offer framework", "Growth operating plan", "Execution roadmap"],
    steps: ["Discovery", "Opportunity assessment", "Model design", "Rollout support"],
    impact: "Clearer priorities, a repeatable growth motion, and fewer disconnected initiatives.",
  },
  {
    slug: "growth-strategy",
    title: "Growth Strategy",
    problem:
      "Adding tactics without structure produces activity, not compounding results.",
    approach:
      "We build a strategy that connects market, offer, channels, and operations into one plan.",
    deliverables: ["Market & segment analysis", "Channel strategy", "Prioritized initiative backlog", "Measurement framework"],
    steps: ["Analysis", "Prioritization", "Planning", "Enablement"],
    impact: "Focused investment on the initiatives most likely to move the business.",
  },
  {
    slug: "desktop-ai-agent-deployment",
    title: "Mythoros© Deployment",
    problem:
      "Generic AI tools rarely understand your processes, documents, or standards.",
    approach:
      "We configure Mythoros©, the Kratos Venture Group Desktop AI Agent, around your workflows, knowledge, permissions, and approved integrations.",
    deliverables: ["Configured Mythoros© appliance", "Skill & permission design", "Integration setup", "Team enablement"],
    steps: ["Discover", "Design", "Deploy", "Evolve"],
    impact: "A dedicated system that supports your team on real, business-specific work.",
  },
  {
    slug: "process-automation",
    title: "Process Automation",
    problem:
      "Repetitive manual work drains capacity and introduces inconsistency.",
    approach:
      "We map workflows, then automate the right steps with human oversight where it matters.",
    deliverables: ["Workflow map", "Automation blueprint", "Implemented automations", "Monitoring & handoffs"],
    steps: ["Map", "Design", "Build", "Monitor"],
    impact: "Reduced admin load and more consistent execution across the team.",
  },
  {
    slug: "sales-infrastructure",
    title: "Sales Infrastructure",
    problem:
      "Leads leak out of pipelines that lack structure, speed, and accountability.",
    approach:
      "We design lead capture, response, and follow-up systems that run reliably.",
    deliverables: ["Pipeline architecture", "Response & follow-up system", "Sales playbooks", "Reporting views"],
    steps: ["Audit", "Design", "Implement", "Optimize"],
    impact: "Faster lead response and more consistent follow-through.",
  },
  {
    slug: "crm-development",
    title: "CRM Development",
    problem:
      "A CRM that mirrors your business is an asset; one that fights it becomes overhead.",
    approach:
      "We configure your CRM around real workflows, data, and reporting needs.",
    deliverables: ["CRM configuration", "Data model & pipelines", "Automation rules", "Dashboards"],
    steps: ["Requirements", "Configuration", "Migration", "Adoption"],
    impact: "Cleaner data and better cross-team visibility.",
  },
  {
    slug: "web-design",
    title: "Web Design",
    problem:
      "A website that doesn't reflect how the business actually operates undersells everything else you build.",
    approach:
      "We design and build websites around your brand, offering, and operating model — not a generic template.",
    deliverables: ["Site architecture & content plan", "Design system & UI", "Built & deployed website", "Analytics & tracking setup"],
    steps: ["Discover", "Design", "Build", "Launch"],
    impact: "A site that represents the business accurately and supports the rest of your systems.",
  },
  {
    slug: "voice-ai",
    title: "Voice AI",
    problem:
      "Customer communication is hard to scale consistently across a growing team.",
    approach:
      "We implement approved-scope voice systems for defined, permissioned interactions.",
    deliverables: ["Use-case definition", "Voice workflow design", "Integration & permissions", "Quality review process"],
    steps: ["Scope", "Design", "Deploy", "Refine"],
    impact: "More consistent, scalable customer communication within approved boundaries.",
  },
  {
    slug: "real-estate-walkthrough-videos",
    title: "Real Estate & Airbnb Marketing",
    problem:
      "Listings and short-term rental properties compete on visibility, not just price — and most owners aren't set up to compete there.",
    approach:
      "We build out marketing for real estate listings and Airbnb/short-term rental properties — video walkthroughs, a real social media presence, and a consistent posting system that keeps listings visible.",
    deliverables: ["Video walkthrough production", "Social media account setup & content system", "Listing & platform optimization (Airbnb, MLS, and similar)", "Ongoing posting & growth management"],
    steps: ["Audit", "Produce", "Launch", "Grow"],
    impact: "Listings and rental properties with a stronger visual presence and steadier social reach.",
  },
  {
    slug: "digital-product-videos-photos",
    title: "Digital Product Videos & Photos",
    problem:
      "Product pages built on flat, inconsistent imagery undersell what you're actually selling.",
    approach:
      "We produce consistent product photography and video suited to your catalog, platform, and use case.",
    deliverables: ["Shot list & style guide", "Studio or on-location capture", "Edited photo & video assets", "Platform-ready exports"],
    steps: ["Brief", "Capture", "Edit", "Deliver"],
    impact: "Product pages and listings that present consistently and convert more reliably.",
  },
  {
    slug: "operational-consulting",
    title: "Operational Consulting",
    problem:
      "Operational drag accumulates quietly until it limits growth.",
    approach:
      "We diagnose where process, data, and systems create friction, then design fixes.",
    deliverables: ["Operations assessment", "Process redesign", "Systems recommendations", "Implementation plan"],
    steps: ["Assess", "Design", "Plan", "Support"],
    impact: "Improved process accountability and better use of business data.",
  },
  {
    slug: "systems-integration",
    title: "Systems Integration",
    problem:
      "Disconnected tools create duplicate work and fragmented information.",
    approach:
      "We connect your systems into one coherent, well-governed operating layer.",
    deliverables: ["Integration map", "Connected data flows", "Governance rules", "Documentation"],
    steps: ["Map", "Connect", "Validate", "Document"],
    impact: "One connected system instead of many disconnected tools.",
  },
  {
    slug: "ongoing-optimization",
    title: "Ongoing Optimization",
    problem:
      "Systems drift as the business changes and quietly lose their edge.",
    approach:
      "We partner over time to measure, refine, and extend what we build.",
    deliverables: ["Performance reviews", "Refinement backlog", "New skill / automation rollouts", "Long-term roadmap"],
    steps: ["Measure", "Prioritize", "Improve", "Extend"],
    impact: "Systems that keep pace with your operation as it evolves.",
  },
];

// Mythoros© — interactive capability tabs.
export const capabilities = [
  {
    id: "sales",
    label: "Sales",
    points: [
      "Respond to inbound leads within approved parameters",
      "Follow up consistently across your pipeline",
      "Draft outreach aligned to your sales scripts",
      "Update CRM records as activity happens",
    ],
  },
  {
    id: "service",
    label: "Customer Service",
    points: [
      "Handle defined, permissioned customer requests",
      "Draft replies aligned to your service standards",
      "Route or escalate based on your rules",
      "Log interactions for full visibility",
    ],
  },
  {
    id: "operations",
    label: "Operations",
    points: [
      "Automate repetitive internal workflows",
      "Monitor scheduled tasks and reminders",
      "Organize business knowledge and documents",
      "Coordinate handoffs across teams",
    ],
  },
  {
    id: "management",
    label: "Management",
    points: [
      "Prepare reports from connected systems",
      "Surface status across pipelines and projects",
      "Support planning with organized data",
      "Track process accountability",
    ],
  },
  {
    id: "research",
    label: "Research",
    points: [
      "Conduct structured research on approved topics",
      "Read and summarize company documents",
      "Compile findings into usable briefs",
      "Support decisions with organized inputs",
    ],
  },
  {
    id: "administration",
    label: "Administration",
    points: [
      "Draft emails and messages for review",
      "Book and manage approved appointments",
      "Maintain organized records",
      "Assist employees with routine tasks",
    ],
  },
];

// Internal architecture layers revealed in the 3D hero.
export const agentLayers = [
  "Intelligence Layer",
  "Business Knowledge",
  "Voice & Communication",
  "Workflow Automation",
  "CRM Integration",
  "Research & Analysis",
  "Secure Permissions",
  "Industry Skill Packs",
];

export const skillPacks = [
  { name: "Automotive Dealership", status: "available" },
  { name: "Sales Development", status: "available" },
  { name: "Customer Service", status: "available" },
  { name: "Office Administration", status: "available" },
  { name: "Management Reporting", status: "available" },
  { name: "Research & Intelligence", status: "available" },
  { name: "Real Estate", status: "coming" },
  { name: "Construction Operations", status: "coming" },
];

export const howItWorks = [
  { num: "01", title: "Discover", text: "We learn how your business actually operates — people, process, and data." },
  { num: "02", title: "Design", text: "We architect the systems, skills, and automations around your workflows." },
  { num: "03", title: "Deploy", text: "We configure, integrate, and roll out with your team and permissions in place." },
  { num: "04", title: "Evolve", text: "We measure, refine, and extend the system as your operation grows." },
];

export const processSteps = [
  { title: "Discovery", text: "Understand goals, constraints, and how work moves through the business." },
  { title: "Workflow Mapping", text: "Document the real workflows, handoffs, and data across teams." },
  { title: "Opportunity Assessment", text: "Identify where systems, automation, and agents create the most impact." },
  { title: "System Architecture", text: "Design the connected operating model and technical approach." },
  { title: "Agent Skill Design", text: "Define agent skills, scope, and approved actions per function." },
  { title: "Integration Planning", text: "Plan integrations with CRM, communication, and business tools." },
  { title: "Configuration", text: "Configure the agent, automations, and permissions to your standards." },
  { title: "Testing", text: "Validate behavior, edge cases, and safeguards before rollout." },
  { title: "Team Training", text: "Enable your team to work alongside the system effectively." },
  { title: "Deployment", text: "Roll out into live operations with monitoring in place." },
  { title: "Optimization", text: "Measure results and refine for accuracy and value." },
  { title: "Long-Term Support", text: "Partner over time to extend skills and adapt to change." },
];

export const industries = [
  { name: "Automotive", note: "Dealership operations, sales, and service coordination." },
  { name: "Professional Services", note: "Client intake, follow-up, and knowledge organization." },
  { name: "Real Estate", note: "Lead response, scheduling, and pipeline visibility." },
  { name: "Finance", note: "Structured processes with strict permission boundaries." },
  { name: "Construction", note: "Project coordination and operational reporting." },
  { name: "Logistics", note: "Scheduling, updates, and cross-team coordination." },
  { name: "Hospitality", note: "Guest communication and operational consistency." },
  { name: "Retail", note: "Customer engagement and repetitive task automation." },
  { name: "Manufacturing", note: "Operational visibility and process accountability." },
];

export const complianceLine =
  "Solutions are configured around each organization's operational, privacy, security, and regulatory requirements.";

export const businessImpact = [
  "Faster lead response",
  "More consistent follow-up",
  "Reduced admin workload",
  "Better cross-team visibility",
  "Fewer repetitive tasks",
  "Scalable customer communication",
  "Improved process accountability",
  "Better use of business data",
];

export const whyKratos = [
  {
    title: "Strategy Before Software",
    text: "We design the operating model first. Tools follow the plan — not the other way around.",
  },
  {
    title: "Built Around Your Business",
    text: "Every system is configured to your workflows, documents, standards, and permissions.",
  },
  {
    title: "Human Oversight",
    text: "Intelligence with a job to do — deployed with human control and clear boundaries.",
  },
  {
    title: "Designed to Evolve",
    text: "We build systems that adapt as your operation changes, not static one-off projects.",
  },
];

export const faqs = [
  { q: "What is Mythoros©?", a: "Mythoros© is the Kratos Venture Group Desktop AI Agent — a dedicated AI business system: a compact desktop appliance configured with your business knowledge, tools, automations, and industry skills to support real work across your operation." },
  { q: "Is it only a chatbot?", a: "No. It can take approved actions across your workflows — following up, drafting communications, updating records, preparing reports, and more — not just answer questions in a chat window." },
  { q: "Does it replace employees?", a: "No. It is designed to support your team, remove repetitive work, and operate under human oversight and defined permissions." },
  { q: "Can it connect to our CRM?", a: "Yes. CRM integration is a core capability. We configure it around your data model, pipelines, and reporting needs." },
  { q: "Can it make and receive calls?", a: "It can make and receive approved calls within defined, permissioned scope as part of your communication workflows." },
  { q: "Can it work with company documents?", a: "Yes. It can read and organize approved company documents to support research, reporting, and knowledge access." },
  { q: "Does everything run locally?", a: "Some functions may operate locally while others may use private cloud infrastructure or approved third-party AI services, depending on the selected architecture and business requirements." },
  { q: "How is access controlled?", a: "Security depends on the final configuration, integrations, permission structure, hosting choices, and third-party providers. Kratos designs the deployment architecture around the client's operational and security requirements." },
  { q: "Can it be customized by industry?", a: "Yes. Industry skill packs and configuration tailor the system to your sector's workflows and standards." },
  { q: "How long does implementation take?", a: "Timelines depend on scope, integrations, and complexity. We define a clear plan during discovery and design." },
  { q: "What happens after deployment?", a: "We partner over time to monitor, refine, and extend the system as your operation evolves." },
  { q: "Can skills be added later?", a: "Yes. Skills, automations, and integrations can be added and refined as your needs change." },
];

export const insights = [
  {
    slug: "what-a-desktop-ai-agent-can-do",
    category: "AI Agents",
    title: "What a Desktop AI Agent Can Actually Do for a Business",
    excerpt: "Beyond the chat window: how a dedicated, business-specific agent supports real operational work under human oversight.",
    outline: [
      "The gap between generic AI tools and business-specific systems",
      "Where a dedicated agent creates practical leverage",
      "Approved-scope actions and human oversight",
      "How capability grows with configuration over time",
    ],
  },
  {
    slug: "why-automation-projects-fail",
    category: "Automation",
    title: "Why Automation Projects Fail Without Process Design",
    excerpt: "Automation amplifies whatever process it runs on. Design the process first, then automate deliberately.",
    outline: [
      "Automating a broken process scales the problem",
      "Mapping workflows before building",
      "Choosing the right steps to automate",
      "Keeping humans in the loop where it matters",
    ],
  },
  {
    slug: "building-an-ai-ready-sales-operation",
    category: "Sales Systems",
    title: "Building an AI-Ready Sales Operation",
    excerpt: "The structural foundations — data, pipeline, and follow-up systems — that make intelligent sales support effective.",
    outline: [
      "Why structure precedes intelligence in sales",
      "Data and pipeline foundations",
      "Response speed and consistent follow-up",
      "Layering intelligent support onto a solid base",
    ],
  },
];

export const insightCategories = [
  "Business Development",
  "AI Agents",
  "Automation",
  "Sales Systems",
  "Operations",
  "Technology Strategy",
];

// Case studies — layout + editable model; content intentionally withheld.
export const caseStudies = [
  { id: "cs-1", sector: "Automotive", title: "Case study coming soon." },
  { id: "cs-2", sector: "Professional Services", title: "Case study coming soon." },
  { id: "cs-3", sector: "Logistics", title: "Case study coming soon." },
];

export const serviceOptions = [
  "Business Development",
  "Mythoros©",
  "Automation",
  "CRM & Sales Systems",
  "Voice AI",
  "Technology Consulting",
  "Other",
];

export const industryOptions = [
  "Automotive", "Professional Services", "Real Estate", "Finance",
  "Construction", "Logistics", "Hospitality", "Retail", "Manufacturing", "Other",
];

export const companySizeOptions = ["1–10", "11–50", "51–200", "201–1000", "1000+"];
export const contactMethodOptions = ["Email", "Phone", "Video call"];

// ---- Pricing ----
export const mythorosTiers = [
  {
    name: "Mythoros© Core",
    price: "$2,498 upfront + $199/month",
    includes: [
      "Mythoros© hardware",
      "Core agent programming",
      "Three custom skills",
      "Basic company knowledge base",
      "One user",
      "Remote onboarding",
      "Essential Care monthly plan",
    ],
    bestFor: "Owners and small businesses starting with one AI agent.",
  },
  {
    name: "Mythoros© Business",
    price: "$4,497 upfront + $399/month",
    includes: [
      "Mythoros© hardware",
      "Advanced core programming",
      "One premium skill pack",
      "Up to eight custom skills",
      "One standard integration",
      "Expanded knowledge base",
      "Business Care monthly plan",
    ],
    bestFor: "Established businesses automating sales, service, or admin.",
  },
  {
    name: "Mythoros© Dealer",
    price: "$4,997 upfront + $499/month",
    includes: [
      "Mythoros© hardware",
      "Core programming",
      "Automotive Dealership Pack (lead response, appointment scheduling, CRM notes, finance-document checklist, inventory & market research, management summary, ten dealership-specific skills)",
      "60-day onboarding and optimization",
    ],
    bestFor: "Dealerships and automotive teams.",
  },
  {
    name: "Enterprise",
    price: "Custom quote — from $12,500 upfront + $1,499/month",
    includes: [
      "Multiple agents / devices",
      "Multiple departments",
      "Custom workflows",
      "Advanced integrations",
      "Role-based permission architecture",
      "Voice / CRM / reporting options",
      "Dedicated implementation",
      "Ongoing development",
      "Custom support agreement",
    ],
    bestFor: "Multi-location businesses and larger deployments.",
  },
];

export const pricingDisclosure =
  "Hardware, programming, and setup fees are billed in advance. Monthly support begins on activation. Third-party subscriptions, API usage, telephony, text messaging, email services, premium AI models, and external software fees are not included unless expressly listed. Final pricing depends on workflow complexity, data preparation, integrations, user count, and usage volume.";

export const servicePricing = {
  "voice-ai": {
    title: "Voice AI pricing",
    packs: [
      {
        name: "Voice Agent Pack",
        price: "$1,499 setup + $299/month, plus usage",
        includes: [
          "One inbound or outbound voice agent",
          "Custom greeting & conversational flow",
          "Company knowledge",
          "Call routing",
          "Appointment-booking workflow",
          "Call summaries / transcripts",
          "Human-transfer rules",
          "Basic CRM / email notification",
          "Consent & opt-out wording",
          "Monthly testing and optimization",
        ],
        usage: {
          label: "Usage",
          lines: [
            "$0.25–$0.45 per connected minute, or bundled plans:",
            "Voice 500 (500 min) — $399/mo",
            "Voice 1,500 (1,500 min) — $699/mo",
            "Voice 3,000 (3,000 min) — $1,199/mo",
            "High Volume — custom quote",
          ],
        },
      },
    ],
  },
  "crm-development": {
    title: "CRM Development pricing",
    packs: [
      {
        name: "CRM Integration Pack",
        price: "Starting at $999 setup + $199 monthly servicing fee",
        includes: [
          "Create / update contacts",
          "Insert conversation notes",
          "Move opportunities",
          "Create tasks",
          "Trigger follow-up",
          "Notify employees",
          "Pipeline summaries",
          "Identify untouched leads",
        ],
        usage: {
          label: "Platform cost",
          lines: ["$149 per user / month — Close.io subscription"],
        },
        calculator: {
          label: "Estimate your monthly cost",
          servicingFee: 199,
          perUser: 149,
          setup: 999,
          defaultUsers: 3,
          maxUsers: 50,
        },
        note: "Complex integrations (custom APIs, dealership systems, unsupported software): $2,500–$7,500, quoted separately.",
      },
    ],
  },
  "sales-infrastructure": {
    title: "Sales Infrastructure pricing",
    packs: [
      {
        name: "Lead Response and Appointment Pack",
        price: "$1,499 setup + $249/month",
        includes: [
          "Website-lead monitoring",
          "Immediate email / SMS response",
          "Lead qualification",
          "Appointment suggestions",
          "Follow-up sequences",
          "Missed-response alerts",
          "CRM updates",
          "Daily lead summary",
          "Human handoff",
        ],
        note: "Third-party SMS / calling / email usage billed additionally.",
      },
      {
        name: "Automotive Dealership Pack",
        price: "$1,999 setup + $249/month",
        includes: [
          "Lead response",
          "Appointment scheduling",
          "CRM notes",
          "Finance-document checklist",
          "Inventory & market research",
          "Management summary",
          "Ten dealership-specific skills",
        ],
      },
      {
        name: "Automotive Pro Pack",
        price: "$3,499 setup + $499/month",
        includes: [
          "Everything in the Automotive Dealership Pack",
          "Additional CRM & dealership-system integrations",
          "Expanded automation workflows",
          "Priority optimization and reporting",
        ],
      },
    ],
  },
  "real-estate-walkthrough-videos": {
    title: "Real Estate & Airbnb Marketing pricing",
    packs: [
      {
        name: "Social Media Assistant",
        price: "$999 setup + $299/month",
        includes: [
          "Caption drafting",
          "Content-calendar creation",
          "Post ideas",
          "Hashtag / keyword research",
          "Review-response drafts",
          "Brand voice configuration",
          "Scheduling integration",
          "Monthly content plan",
          "Up to 3 social channels",
        ],
      },
      {
        name: "Social Media Managed",
        price: "$1,499 setup + $599/month",
        includes: [
          "Everything in Social Media Assistant, plus:",
          "Up to 20 prepared posts monthly",
          "Scheduling & publishing",
          "Basic creative templates",
          "Comment / inbox monitoring",
          "Monthly performance report",
          "Competitor-content monitoring",
          "One monthly planning meeting",
        ],
      },
    ],
  },
  "operational-consulting": {
    title: "Operational Consulting pricing",
    packs: [
      {
        name: "Research and Market Intelligence Pack",
        price: "$999 setup + $149/month",
        includes: [
          "Competitor monitoring",
          "Public web research",
          "Market summaries",
          "Product / inventory comparisons",
          "Pricing reports",
          "Scheduled intelligence briefs",
          "Source-linked research",
          "Custom reporting format",
        ],
      },
      {
        name: "Executive Assistant Pack",
        price: "$999 setup + $199/month",
        includes: [
          "Email drafting",
          "Calendar assistance",
          "Meeting preparation",
          "Daily priority brief",
          "Follow-up reminders",
          "Document summaries",
          "Task organization",
          "Research assistance",
          "Contact / meeting notes",
        ],
      },
      {
        name: "Finance Analyst Pack",
        price: "$1,499 setup + $149/month",
        includes: [
          "Financial-statement summarization",
          "Expense categorization",
          "Cash-flow reporting",
          "Budget-vs-actual comparisons",
          "AR follow-up lists",
          "Monthly management summaries",
          "Spreadsheet analysis",
          "Financial KPI dashboard prep",
          "Custom report templates",
        ],
        disclosure:
          "Mythoros© assists with financial analysis and reporting. It does not replace a licensed accountant, financial adviser, or compliance professional.",
      },
    ],
  },
  "process-automation": {
    title: "Process Automation pricing",
    packs: [
      {
        name: "Custom Skill Development",
        price: "Priced per skill",
        includes: [
          "Simple skill: $250–$500",
          "Standard skill: $500–$1,000",
          "Advanced multi-step skill: $1,000–$2,500",
          "Integration-dependent workflow: $1,500–$5,000+",
        ],
      },
      {
        name: "Knowledge Base Pack",
        price: "$500–$2,500 one time, based on volume",
        includes: [
          "Up to 50 documents — $500",
          "Up to 250 documents — $1,250",
          "Up to 1,000 documents — $2,500",
          "Larger volumes — custom",
        ],
      },
    ],
  },
  "desktop-ai-agent-deployment": {
    title: "Mythoros© deployment pricing",
    packs: [
      {
        name: "Mythoros© hardware",
        price: "$999 one time",
        includes: [
          "Compact desktop hardware",
          "Mythoros© branding",
          "OS preparation",
          "Security / software updates completed before delivery",
          "Remote-access configuration",
          "One-year limited hardware support",
        ],
        note: "Shipping charged separately.",
      },
      {
        name: "Core Agent Programming",
        price: "Starting at $1,499 one time",
        includes: [
          "OmniGrip© agent installation and configuration",
          "Primary AI model connection",
          "One company profile",
          "One primary user",
          "Basic knowledge base (up to 25 uploaded documents)",
          "Email drafting assistance",
          "Web research tools",
          "Calendar / task assistance",
          "Basic scheduled tasks",
          "Three custom skills",
          "Permission / approval settings",
          "Remote onboarding",
          "30 days launch support",
        ],
      },
      {
        name: "Monthly Care Plans",
        price: "From $199/month",
        plans: [
          { name: "Essential Care", price: "$199/mo", points: ["Monitoring & security updates", "Minor skill adjustments", "Email support", "Monthly check-in"] },
          { name: "Business Care", price: "$399/mo", points: ["Everything in Essential Care", "Priority support", "New skill rollouts", "Integration maintenance", "Monthly optimization review"] },
          { name: "Managed Agent", price: "$799/mo", points: ["Everything in Business Care", "Proactive management", "Expanded skill development", "Performance reporting", "Dedicated point of contact"] },
          { name: "Enterprise", price: "From $1,499/mo", points: ["Everything in Managed Agent", "Multi-device / multi-department support", "Custom development hours", "SLA-backed support", "Roadmap planning"] },
        ],
      },
    ],
  },
};
